import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../hooks/useApi';
import DateInput from '../components/DateInput';
import Icon from '../components/Icon';
import './AvancesFonds.css';

const ASSOCIES = ['LCU', 'ELA'];
const STATUTS = ['en_cours', 'partiellement_remboursee', 'remboursee'];
const DESTINATIONS_ESCAMEL = ['Compte Escamel', 'Cash Escamel'];
const SOURCES_PERSO = {
  LCU: ['Compte LCU', 'Cash LCU'],
  ELA: ['Compte ELA', 'Cash ELA'],
};

function fmtMontant(value) {
  return `${Number(value || 0).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
}

function libelleStatut(statut) {
  if (statut === 'en_cours') return 'En cours';
  if (statut === 'partiellement_remboursee') return 'Part. remboursée';
  if (statut === 'remboursee') return 'Remboursée';
  return statut;
}

function classeStatut(statut) {
  if (statut === 'en_cours') return 'badge--warning';
  if (statut === 'partiellement_remboursee') return 'badge--info';
  if (statut === 'remboursee') return 'badge--success';
  return '';
}

function aujourdhui() {
  return new Date().toISOString().slice(0, 10);
}

// ─── MODAL SHELL ──────────────────────────────────────────────────────────────
function ModalShell({ title, onClose, children }) {
  return (
    <div className="af-modal-overlay" onClick={onClose}>
      <div className="af-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="af-modal-header">
          <h3 className="af-modal-title">{title}</h3>
          <button className="af-modal-close" onClick={onClose} aria-label="Fermer">
            <Icon name="fermer" size={16} />
          </button>
        </div>
        <div className="af-modal-body">{children}</div>
      </div>
    </div>
  );
}

// ─── MODAL NOUVELLE AVANCE ────────────────────────────────────────────────────
function ModalNouvelleAvance({ onClose, onSaved }) {
  const [form, setForm] = useState({
    associe: 'LCU',
    date_avance: aujourdhui(),
    montant: '',
    source: SOURCES_PERSO.LCU[0],
    destination: DESTINATIONS_ESCAMEL[0],
    description: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const sources = SOURCES_PERSO[form.associe];

  useEffect(() => {
    if (!sources.includes(form.source)) {
      setForm((prev) => ({ ...prev, source: sources[0] }));
    }
  }, [form.associe, form.source, sources]);

  function patch(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/avances', { ...form, montant: Number(form.montant || 0) });
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title="Nouvelle avance de fonds" onClose={onClose}>
      <form className="af-form" onSubmit={submit}>
        <div className="af-form-grid">
          <div className="af-field">
            <label>Associé</label>
            <select value={form.associe} onChange={(e) => patch('associe', e.target.value)}>
              {ASSOCIES.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="af-field">
            <label>Date</label>
            <DateInput value={form.date_avance} onChange={(v) => patch('date_avance', v)} required />
          </div>
          <div className="af-field">
            <label>Montant</label>
            <input type="number" min="0" step="0.01" value={form.montant} onChange={(e) => patch('montant', e.target.value)} required />
          </div>
          <div className="af-field">
            <label>Source (argent perso)</label>
            <select value={form.source} onChange={(e) => patch('source', e.target.value)}>
              {sources.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="af-field">
            <label>Destination (Escamel)</label>
            <select value={form.destination} onChange={(e) => patch('destination', e.target.value)}>
              {DESTINATIONS_ESCAMEL.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="af-field af-span-2">
            <label>Description</label>
            <input type="text" value={form.description} onChange={(e) => patch('description', e.target.value)} />
          </div>
          <div className="af-field af-span-2">
            <label>Notes</label>
            <textarea rows="3" value={form.notes} onChange={(e) => patch('notes', e.target.value)} />
          </div>
        </div>
        <div className="af-modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Enregistrement...' : "Créer l'avance"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─── MODAL REMBOURSEMENT ──────────────────────────────────────────────────────
function ModalRemboursement({ avance, onClose, onSaved }) {
  const [form, setForm] = useState({
    date_remboursement: aujourdhui(),
    montant: avance.solde_restant,
    source_remb: DESTINATIONS_ESCAMEL[0],
    destination_remb: SOURCES_PERSO[avance.associe][0],
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const destinations = SOURCES_PERSO[avance.associe];

  function patch(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/avances/${avance.id}/rembourser`, { ...form, montant: Number(form.montant || 0) });
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title={`Remboursement — avance #${avance.id}`} onClose={onClose}>
      <form className="af-form" onSubmit={submit}>
        <div className="af-readonly-grid">
          <div className="af-readonly-card">
            <span className="muted">Associé</span>
            <strong>{avance.associe}</strong>
          </div>
          <div className="af-readonly-card">
            <span className="muted">Montant original</span>
            <strong style={{ fontFeatureSettings: "'tnum' 1" }}>{fmtMontant(avance.montant)}</strong>
          </div>
          <div className="af-readonly-card">
            <span className="muted">Solde restant</span>
            <strong className="gold" style={{ fontFeatureSettings: "'tnum' 1" }}>{fmtMontant(avance.solde_restant)}</strong>
          </div>
        </div>
        <div className="af-form-grid">
          <div className="af-field">
            <label>Date remboursement</label>
            <DateInput value={form.date_remboursement} onChange={(v) => patch('date_remboursement', v)} required />
          </div>
          <div className="af-field">
            <label>Montant à rembourser</label>
            <input type="number" min="0" max={avance.solde_restant} step="0.01" value={form.montant} onChange={(e) => patch('montant', e.target.value)} required />
          </div>
          <div className="af-field">
            <label>Source (Escamel)</label>
            <select value={form.source_remb} onChange={(e) => patch('source_remb', e.target.value)}>
              {DESTINATIONS_ESCAMEL.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="af-field">
            <label>Destination (perso)</label>
            <select value={form.destination_remb} onChange={(e) => patch('destination_remb', e.target.value)}>
              {destinations.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="af-field af-span-2">
            <label>Notes</label>
            <textarea rows="3" value={form.notes} onChange={(e) => patch('notes', e.target.value)} />
          </div>
        </div>
        <div className="af-modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Enregistrement...' : 'Confirmer le remboursement'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─── SKELETON ─────────────────────────────────────────────────────────────────
function AvancesSkeleton() {
  return (
    <div className="af-page">
      <div className="af-header">
        <div>
          <div className="skeleton" style={{ height: 34, width: 280, marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 12, width: 380 }} />
        </div>
        <div className="skeleton" style={{ height: 36, width: 130, borderRadius: 'var(--radius-md)' }} />
      </div>
      <div className="af-kpis">
        {[0, 1].map((i) => (
          <div key={i} className="card af-kpi-card">
            <div className="skeleton" style={{ height: 12, width: 140, marginBottom: 'var(--space-3)' }} />
            <div className="skeleton" style={{ height: 28, width: 100 }} />
          </div>
        ))}
      </div>
      <div className="card" style={{ padding: 'var(--space-6)' }}>
        {[0, 1, 2].map((i) => (
          <div key={i} className="skeleton" style={{ height: 14, width: '100%', marginBottom: 'var(--space-3)' }} />
        ))}
      </div>
    </div>
  );
}

// ─── PAGE PRINCIPALE ──────────────────────────────────────────────────────────
export default function AvancesFonds() {
  const anneeCourante = new Date().getFullYear();
  const [annee, setAnnee] = useState(anneeCourante);
  const [associeFiltre, setAssocieFiltre] = useState('');
  const [statutFiltre, setStatutFiltre] = useState('');
  const [avances, setAvances] = useState([]);
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [expanded, setExpanded] = useState({});
  const [modalNouvelle, setModalNouvelle] = useState(false);
  const [modalRemb, setModalRemb] = useState(null);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ annee: String(annee) });
      if (associeFiltre) params.set('associe', associeFiltre);
      if (statutFiltre) params.set('statut', statutFiltre);
      const [avancesData, resumeData] = await Promise.all([
        api.get(`/avances?${params.toString()}`),
        api.get(`/avances/resume/${annee}`),
      ]);
      setAvances(avancesData);
      setResume(resumeData);
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [annee, associeFiltre, statutFiltre]);

  useEffect(() => { charger(); }, [charger]);

  function showToast(message) {
    setToast(message);
    setTimeout(() => setToast(''), 3500);
  }

  async function supprimerAvance(id) {
    if (!window.confirm('Supprimer cette avance de fonds ?')) return;
    try {
      await api.delete(`/avances/${id}`);
      showToast('Avance supprimée');
      charger();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  }

  const kpis = useMemo(() => resume || { LCU: { solde_en_suspens: 0 }, ELA: { solde_en_suspens: 0 } }, [resume]);

  if (loading) return <AvancesSkeleton />;

  return (
    <div className="af-page">

      {/* ── En-tête ────────────────────────────────────────────── */}
      <div className="af-header">
        <div className="af-header-text">
          <h1 className="page-title">Avances de fonds</h1>
          <p className="page-subtitle muted">Prêts personnels des associés à Escamel — suivi jusqu'au remboursement complet.</p>
        </div>
        <div className="af-header-actions">
          <select
            className="af-annee-sel"
            value={annee}
            onChange={(e) => setAnnee(Number(e.target.value))}
          >
            {[anneeCourante, anneeCourante - 1, anneeCourante - 2].map((v) => (
              <option key={v} value={v}>FY{v}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={() => setModalNouvelle(true)}>
            <Icon name="ajouter" size={14} /> Nouvelle avance
          </button>
        </div>
      </div>

      {/* ── Toast ─────────────────────────────────────────────── */}
      {toast && (
        <div className="af-toast">
          <Icon name="succes" size={15} /> {toast}
        </div>
      )}

      {/* ── KPIs ──────────────────────────────────────────────── */}
      <div className="af-kpis">
        {ASSOCIES.map((assoc) => {
          const solde = kpis[assoc]?.solde_en_suspens || 0;
          return (
            <div key={assoc} className="card af-kpi-card rise-in">
              <div className="kpi-top">
                <span className="kpi-icon"><Icon name="encaisse" size={18} /></span>
                <span className="kpi-label">Solde en suspens {assoc}</span>
              </div>
              <div className={`af-kpi-value${solde > 0 ? ' gold' : ' success'}`} style={{ fontFeatureSettings: "'tnum' 1, 'lnum' 1", fontFamily: "'Cinzel', serif" }}>
                {fmtMontant(solde)}
              </div>
              <div className="kpi-sub">{solde > 0 ? 'À rembourser' : 'Aucune avance en suspens'}</div>
            </div>
          );
        })}
        <div className="card af-kpi-card rise-in">
          <div className="kpi-top">
            <span className="kpi-icon"><Icon name="argent" size={18} /></span>
            <span className="kpi-label">Total en suspens FY{annee}</span>
          </div>
          <div className="af-kpi-value gold" style={{ fontFeatureSettings: "'tnum' 1, 'lnum' 1", fontFamily: "'Cinzel', serif" }}>
            {fmtMontant((kpis.LCU?.solde_en_suspens || 0) + (kpis.ELA?.solde_en_suspens || 0))}
          </div>
          <div className="kpi-sub muted">LCU + ELA combiné</div>
        </div>
      </div>

      {/* ── Tableau principal ──────────────────────────────────── */}
      <div className="card af-table-card">
        <div className="card-header">
          <div className="card-header-left">
            <span className="card-header-icon"><Icon name="liste" size={16} /></span>
            <span className="card-title">Registre des avances</span>
          </div>
          <div className="af-filters">
            <select
              className="af-filter-sel"
              value={associeFiltre}
              onChange={(e) => setAssocieFiltre(e.target.value)}
            >
              <option value="">Tous les associés</option>
              {ASSOCIES.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <select
              className="af-filter-sel"
              value={statutFiltre}
              onChange={(e) => setStatutFiltre(e.target.value)}
            >
              <option value="">Tous les statuts</option>
              {STATUTS.map((s) => <option key={s} value={s}>{libelleStatut(s)}</option>)}
            </select>
          </div>
        </div>

        <div className="af-table-wrap">
          <table className="esc-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Associé</th>
                <th>Montant</th>
                <th>Remboursé</th>
                <th>Solde</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {avances.map((avance) => {
                const isExpanded = !!expanded[avance.id];
                const suppressible = avance.statut === 'en_cours' && avance.remboursements.length === 0;
                return (
                  <React.Fragment key={avance.id}>
                    <tr
                      className="af-row-main"
                      onClick={() => setExpanded((prev) => ({ ...prev, [avance.id]: !prev[avance.id] }))}
                    >
                      <td>{avance.date_avance}</td>
                      <td>
                        <span className="badge" style={{ background: avance.associe === 'LCU' ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'color-mix(in srgb, var(--info) 15%, transparent)', color: avance.associe === 'LCU' ? 'var(--accent)' : 'var(--info)' }}>
                          {avance.associe}
                        </span>
                      </td>
                      <td style={{ fontFeatureSettings: "'tnum' 1" }}>{fmtMontant(avance.montant)}</td>
                      <td style={{ fontFeatureSettings: "'tnum' 1" }}>{fmtMontant(avance.montant_rembourse)}</td>
                      <td className="gold" style={{ fontFeatureSettings: "'tnum' 1", fontWeight: 600 }}>{fmtMontant(avance.solde_restant)}</td>
                      <td>
                        <span className={`badge ${classeStatut(avance.statut)}`}>{libelleStatut(avance.statut)}</span>
                      </td>
                      <td className="af-actions-cell" onClick={(e) => e.stopPropagation()}>
                        {avance.statut !== 'remboursee' && (
                          <button className="btn btn-primary btn-sm" onClick={() => setModalRemb(avance)}>
                            Rembourser
                          </button>
                        )}
                        {suppressible && (
                          <button className="btn btn-danger btn-sm" onClick={() => supprimerAvance(avance.id)}>
                            <Icon name="supprimer" size={13} />
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="af-row-expanded">
                        <td colSpan="7">
                          <div className="af-expanded">
                            <div className="af-expanded-meta">
                              <div className="af-readonly-card">
                                <span className="muted">Source</span>
                                <strong>{avance.source}</strong>
                              </div>
                              <div className="af-readonly-card">
                                <span className="muted">Destination</span>
                                <strong>{avance.destination}</strong>
                              </div>
                              <div className="af-readonly-card">
                                <span className="muted">Description</span>
                                <strong>{avance.description || '—'}</strong>
                              </div>
                            </div>
                            <div className="af-remboursements">
                              <div className="af-remb-title">
                                <Icon name="recu" size={14} /> Remboursements
                              </div>
                              {avance.remboursements.length === 0 ? (
                                <div className="empty-state" style={{ padding: 'var(--space-4)', textAlign: 'left' }}>
                                  Aucun remboursement enregistré.
                                </div>
                              ) : (
                                <div className="af-sub-table-wrap">
                                  <table className="esc-table">
                                    <thead>
                                      <tr>
                                        <th>Date</th>
                                        <th>Montant</th>
                                        <th>Source</th>
                                        <th>Destination</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {avance.remboursements.map((remb) => (
                                        <tr key={remb.id}>
                                          <td>{remb.date_remboursement}</td>
                                          <td style={{ fontFeatureSettings: "'tnum' 1" }}>{fmtMontant(remb.montant)}</td>
                                          <td>{remb.source_remb}</td>
                                          <td>{remb.destination_remb}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {avances.length === 0 && (
                <tr>
                  <td colSpan="7" className="empty-state" style={{ padding: 'var(--space-7)' }}>
                    Aucune avance pour ces filtres.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Tableau comptable ──────────────────────────────────── */}
      <div className="card af-fiscal-card">
        <div className="card-header">
          <div className="card-header-left">
            <span className="card-header-icon"><Icon name="banque" size={16} /></span>
            <span className="card-title">Pour le comptable — Avances en suspens au 31 décembre</span>
          </div>
          <span className="muted" style={{ fontSize: 12 }}>FY{annee}</span>
        </div>
        <div className="af-table-wrap">
          <table className="esc-table">
            <thead>
              <tr>
                <th>Associé</th>
                <th>Total avancé</th>
                <th>Total remboursé</th>
                <th>Solde en suspens</th>
              </tr>
            </thead>
            <tbody>
              {ASSOCIES.map((associe) => (
                <tr key={associe}>
                  <td>
                    <span className="badge" style={{ background: associe === 'LCU' ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'color-mix(in srgb, var(--info) 15%, transparent)', color: associe === 'LCU' ? 'var(--accent)' : 'var(--info)' }}>
                      {associe}
                    </span>
                  </td>
                  <td style={{ fontFeatureSettings: "'tnum' 1" }}>{fmtMontant(resume?.[associe]?.total_avance)}</td>
                  <td style={{ fontFeatureSettings: "'tnum' 1" }}>{fmtMontant(resume?.[associe]?.total_rembourse)}</td>
                  <td className="gold" style={{ fontFeatureSettings: "'tnum' 1", fontWeight: 600 }}>{fmtMontant(resume?.[associe]?.solde_en_suspens)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="af-fiscal-note muted">
          Les avances non remboursées sont déclarées à titre informatif sur la feuille fiscale de chaque associé.
        </p>
      </div>

      {/* ── Modals ─────────────────────────────────────────────── */}
      {modalNouvelle && (
        <ModalNouvelleAvance
          onClose={() => setModalNouvelle(false)}
          onSaved={() => { setModalNouvelle(false); showToast('Avance créée'); charger(); }}
        />
      )}
      {modalRemb && (
        <ModalRemboursement
          avance={modalRemb}
          onClose={() => setModalRemb(null)}
          onSaved={() => { setModalRemb(null); showToast('Remboursement enregistré'); charger(); }}
        />
      )}
    </div>
  );
}
